import { createGlobalStyle } from 'styled-components'

import {
  RC_COLLAPSE_LIST_POINT,
  RC_PHONE_POINT,
  RC_STACK_ROW_CARDS,
  RC_TABLET_POINT,
} from './utils/breakpoints.constants'

// DS tokens: @ironscales/design-system/styles. Host/page tokens: host-tokens.js (RootContainer / withAppChrome).
export const RootStyles = createGlobalStyle`

/* Base UI setup: prevent overflow at document level - scrolling happens in AppLayout ContentArea */
html, body {
  overflow: hidden;
  max-width: 100vw;
}

/* Base UI setup: body needs position relative for iOS 26+ Safari backdrop support */
body {
  position: relative;
  padding-top: 0;
}

.app-content {
  height: 100%;
  min-height: 0;
}

.app-content > * {
  height: 100%;
  min-height: 0;
}

.root {
  height: 100%;
  overflow: auto;
  background: var(--color-background);
  /* Base UI setup: Creates a separate stacking context so portaled popups appear above page contents */
  isolation: isolate;
}

/* Under the platform nav ContentArea is the page scroller and owns the
   --page-inset gutter. The legacy .root above is height:100% + overflow:auto,
   so it wins as the innermost scroller and paints its scrollbar INSIDE that
   20px padding, leaving the bar floating 20px off the window edge. Hand
   scrolling back to ContentArea; min-height keeps short pages filling the
   viewport. Old nav keeps .root as its scroller. */
.platform-nav-content .root {
  height: auto;
  min-height: 100%;
  overflow: visible;
}

.root > .content-container {
  width: 80%;
  margin: var(--base-margin) auto;
}

/* Under the platform nav the content scroller owns the page gutter
   (--page-inset), so legacy pages fill the width instead of the old 80% centered
   column (which read as an unintended centered card, e.g. Activities/Resources).
   Scoped to the new nav so the legacy nav keeps its centered layout. */
.platform-nav-content .root > .content-container {
  width: auto;
  margin: 0;
}

/* Sub-dashboards (Mitigation / Awareness / ADE, and the legacy home) wrap their
   body in a 30px-padded div. Under the platform nav the content scroller owns the
   side gutter (--page-inset), so the horizontal 30px must drop or it stacks to
   50px vs the 20px every other page shows. Vertical padding stays (spacing under
   the GlobalFilters bar). Old nav keeps the full 30px. */
.dashboard-page-body {
  padding: 30px;
}
.platform-nav-content .dashboard-page-body {
  padding-left: 0;
  padding-right: 0;
}

.content-container-with-menu {
  height: 100%; overflow: auto;
}

/* New platform nav: ContentArea / .root is the page scroller. Title + tabs must
   scroll away with the page; only a table header pins. The legacy rule above
   locks this wrapper as an inner scroller (height 100% + overflow auto), which
   keeps the title/tab strip on screen. Grow with content instead. Incident
   *detail* split-panes (absolute list + report) still need a bounded pane —
   see resolution.styles.js :has(.resolution-list-container). */
.platform-nav-content .content-container-with-menu {
  height: auto;
  min-height: 0;
  overflow: visible;
}

.layout-with-menu {
  display: grid;
  grid-template-columns: 248px minmax(0, 1fr);
  height: 100%; overflow: hidden;
}

.layout-with-menu > .content-container-with-menu {
  margin: 0;
  min-width: 0;
}

@media screen and (max-width: ${RC_TABLET_POINT}) {
  .layout-with-menu {
    grid-template-rows: auto minmax(0, 1fr);
    grid-template-columns: 1fr;
    align-content: start;
    align-items: start;
    overflow: visible;
  }

  .layout-with-menu > .side-menu {
    grid-row: 1;
  }

  .layout-with-menu > .content-container-with-menu {
    grid-row: 2;
  }

  .content-container-with-menu {
    height: 100%;
    margin: 0;
  }
}

/* HRM PLG promo page (full-bleed). HrmPlgPage toggles the hrm-plg-page-active
   class on <body> while mounted. It replaces the content of sections that
   normally show a left side navigation (Awareness Center Menu / dashboard
   PageWithSidebar) — hide that nav and let the promo use the full width. */
body.hrm-plg-page-active .layout-with-menu {
  grid-template-columns: minmax(0, 1fr);
}
body.hrm-plg-page-active .side-menu {
  display: none;
}
body.hrm-plg-page-active .page-with-sidebar > :first-child {
  display: none;
}

.content-container-with-menu .partner-companyFilter {
  width: 200px;
  margin-bottom: var(--base-margin);
}

@supports (-webkit-touch-callout: none) {
  .partner-companyFilter input#companies_select {
    font-size: 16px !important;
  }
}

.loader-hidden {
  display: none;
}

.loader-active {
  display: block;
}

.loader-container {
  height: 100%;
  position: relative;
}

.loader-container > * {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}



.loader-container > * .ball-beat {
  margin-left: -30px;
}

@-webkit-keyframes ball-spin-fade-loader {
  50% {
    opacity: 0.3;
    -webkit-transform: scale(0.4);
    transform: scale(0.4);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}

@keyframes ball-spin-fade-loader {
  50% {
    opacity: 0.3;
    transform: scale(0.4);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

.ball-spin-fade-loader {
  position: relative;
  top: -10px;
  left: -10px;
}

.ball-spin-fade-loader > div:nth-child(1) {
  top: 25px;
  left: 0;
  -webkit-animation: ball-spin-fade-loader 1s -0.96s infinite linear;
  animation: ball-spin-fade-loader 1s -0.96s infinite linear;
}

.ball-spin-fade-loader > div:nth-child(2) {
  top: 17.04545px;
  left: 17.04545px;
  -webkit-animation: ball-spin-fade-loader 1s -0.84s infinite linear;
  animation: ball-spin-fade-loader 1s -0.84s infinite linear;
}

.ball-spin-fade-loader > div:nth-child(3) {
  top: 0;
  left: 25px;
  -webkit-animation: ball-spin-fade-loader 1s -0.72s infinite linear;
  animation: ball-spin-fade-loader 1s -0.72s infinite linear;
}

.ball-spin-fade-loader > div:nth-child(4) {
  top: -17.04545px;
  left: 17.04545px;
  -webkit-animation: ball-spin-fade-loader 1s -0.6s infinite linear;
  animation: ball-spin-fade-loader 1s -0.6s infinite linear;
}

.ball-spin-fade-loader > div:nth-child(5) {
  top: -25px;
  left: 0;
  -webkit-animation: ball-spin-fade-loader 1s -0.48s infinite linear;
  animation: ball-spin-fade-loader 1s -0.48s infinite linear;
}

.ball-spin-fade-loader > div:nth-child(6) {
  top: -17.04545px;
  left: -17.04545px;
  -webkit-animation: ball-spin-fade-loader 1s -0.36s infinite linear;
  animation: ball-spin-fade-loader 1s -0.36s infinite linear;
}

.ball-spin-fade-loader > div:nth-child(7) {
  top: 0;
  left: -25px;
  -webkit-animation: ball-spin-fade-loader 1s -0.24s infinite linear;
  animation: ball-spin-fade-loader 1s -0.24s infinite linear;
}

.ball-spin-fade-loader > div:nth-child(8) {
  top: 17.04545px;
  left: -17.04545px;
  -webkit-animation: ball-spin-fade-loader 1s -0.12s infinite linear;
  animation: ball-spin-fade-loader 1s -0.12s infinite linear;
}

.ball-spin-fade-loader > div {
  width: 15px;
  height: 15px;
  margin: 2px;
  border-radius: 100%;
  position: absolute;
  background-color: var(--primary-color);
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}

.o0 {
  opacity: 0;
}

.o05 {
  opacity: 0.5;
}

.dfi {
  display: flex !important;
}

.dots {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.flex-container {
  display: flex;
  align-content: center;
  align-items: center;
  justify-content: space-between;
}

.ha {
  height: auto;
}

.cd,
.cdi {
  cursor: default !important;
}

.bni {
  border: none !important;
}

.cp {
  cursor: pointer;
}

.cb {
  clear: both;
}

.lh-1 {
  line-height: 1;
}

.p-10 {
  padding: 10px;
}

.p-15 {
  padding: 15px;
}

.p-20 {
  padding: 20px;
}

.pt-20 {
  padding-top: 20px;
}

.pti-20 {
  padding-top: 20px !important;
}

.pti-60 {
  padding-top: 60px !important;
}

.bb-1r {
  border-bottom: 1px solid #ff0000;
}

.pz,
.pzi {
  padding: 0 !important;
}

.usn {
  user-select: none;
}

.fr {
  float: right;
}

.fl {
  float: left;
}

.oh {
  overflow: hidden !important;
}

.fw-b,
.b {
  font-weight: bold;
}

.fw-600,
.sb {
  font-weight: 600;
}

.tal {
  text-align: left;
}

.tac {
  text-align: center;
}

.tar {
  text-align: right;
}

.dn {
  display: none;
}

.dni {
  display: none !important;
}

.dn-pdf {
  position: absolute;
  left: -9999px;
}

.mt {
  margin-top: var(--base-margin);
}

.mr {
  margin-right: var(--base-margin);
}

.mr10 {
  margin-right: 10px;
}

.ml {
  margin-left: var(--base-margin);
}

.sml {
  margin-left: calc(var(--base-margin) / 3);
}

.smr {
  margin-right: calc(var(--base-margin) / 3);
}

.smt {
  margin-top: calc(var(--base-margin) / 3);
}

.mb {
  margin-bottom: var(--base-margin);
}

.mb-5 {
  margin-bottom: 5px;
}

.mb-10 {
  margin-bottom: 10px;
}

.mbz {
  margin-bottom: 0;
}

.mbzi {
  margin-bottom: 0 !important;
}

.mtz {
  margin-top: 0;
}

.mz {
  margin: 0;
}

.mzi {
  margin: 0 !important;
}

.mtb-10 {
  margin: 10px 0;
}

.mtb-20 {
  margin: 20px 0;
}

.m15 {
  margin: 15px;
}

.bp {
  padding: var(--base-margin);
}

.br-r {
  border: 1px solid #ff0000;
}

.br-g {
  border: 1px solid green;
}

.br-b {
  border: 1px solid blue;
}

.br-blk {
  border: 1px solid #000;
}

.brr {
  border-right: 1px solid #ebebeb;
}

.df {
  display: flex;
}

.fw {
  flex-wrap: wrap;
}

.fone {
  flex: 1;
}

.aic {
  align-items: center;
}

.cap {
  text-transform: capitalize;
}

.upper {
  text-transform: uppercase;
}

.lower {
  text-transform: lowercase;
}

.warning-color {
  color: var(--warning-color);
}

.negative-color {
  color: var(--negative-color);
}

.red {
  color: var(--red);
}

.orange {
  color: orange;
}

.blue {
  color: var(--primary-color);
}

.dib-vac {
  display: inline-block;
  vertical-align: middle;
}

.pr {
  position: relative;
}

.db {
  display: block;
}

.vat {
  vertical-align: top !important;
}

.fss {
  font-size: 0.8em;
}

.ptz {
  padding-top: 0 !important;
}

.plz {
  padding-left: 0 !important;
}

.link-color {
  color: var(--secondary-color);
}

.white {
  color: #ffffff;
}

.grey {
  color: #a0a0a0;
}

.ma {
  margin: auto;
}

.rtl {
  direction: rtl;
}

.pc {
  color: var(--primary-color);
}

.l-pc {
  color: var(--sub-text-color);
}

.ws {
  white-space: normal;
}

.ws-nw {
  white-space: nowrap;
}

.w15 {
  width: 15px;
}

.w18 {
  width: 18px;
}

.w20 {
  width: 20px;
}

.wa {
  width: auto !important;
}

.secondary-label {
  color: #1786d6;
  font-weight: 600;
  font-size: 12px;
  min-height: 16px;
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
  background: #d6f2ff;
  text-align: center;
}

.styled-scrollbar::-webkit-scrollbar {
  width: 4px;
  background: var(--color-surface);
}

.styled-scrollbar::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: #aaabb8;
}

.scroll-hover-only {
  padding-right: 4px;
  overflow-y: hidden;
}

.scroll-hover-only:hover {
  padding-right: 0;
  overflow-y: auto;
}

.horizontal-styled-scrollbar {
  padding-bottom: 5px;
}

.horizontal-styled-scrollbar::-webkit-scrollbar {
  height: 10px;
}

.horizontal-styled-scrollbar::-webkit-scrollbar-track {
  background: #f7faff;
}

.horizontal-styled-scrollbar::-webkit-scrollbar-thumb {
  background: #c8d2e1;
}

.horizontal-styled-scrollbar::-webkit-scrollbar-thumb:hover {
  background: #b5becb;
}

.disabled-section {
  cursor: not-allowed;
}

.disabled-section * {
  color: #c5c7d0 !important;
  pointer-events: none !important;
}

.disabled-section * .label-name,
.disabled-section .label-name {
  color: var(--ds-gray-4) !important;
}

.disabled-section .is-input,
.disabled-section .ant-select-selector {
  border-bottom: 1px solid #c9c6c5 !important;
}

.disabled-section img {
  opacity: 0.5;
}

.disabled-section .whiteText {
  color: #ffffff !important;
}

.disabled-section #logo_uploader {
  opacity: 0.6;
}

.link-new-design {
  color: #00afff;
  cursor: pointer;
}

.link-new-design:hover {
  color: #00afff;
  text-decoration: underline;
}

@media screen and (max-width: ${RC_PHONE_POINT}) {
  .gt-p {
    display: none !important;
  }
}

@media screen and (max-width: ${RC_TABLET_POINT}) {
  .gt-t {
    display: none !important;
  }
}

@media screen and (max-width: ${RC_COLLAPSE_LIST_POINT}) {
  .gt-rcCollapseListPoint {
    display: none !important;
  }
}

@media screen and (min-width: ${RC_PHONE_POINT}) {
  .lt-p {
    display: none !important;
  }
}

@media screen and (max-width: ${RC_STACK_ROW_CARDS}) {
  .lt-rc-src {
    display: none !important;
  }
}

@media (pointer: coarse) {
  .dn-ts {
    display: none !important;
  }
}

a,
a:focus {
  color: var(--links-color);
  font-size: 14px;
  text-decoration: none;
}

a:hover,
a:focus:hover {
  color: var(--links-color);
  text-decoration: underline;
}

.ant-select-item-option-content .option {
  display: flex;
  align-items: center;
}

.ant-select-dropdown .ant-select-item-option-state {
  display: none;
}

.ant-select-dropdown {
  color: var(--primary-color);
  padding: 8px;
  z-index: 1050 !important;
}

.ant-select-dropdown .ant-select-item {
  color: var(--primary-color);
  padding: 7px 8px;
  border-radius: 4px;
}

.ant-select-dropdown .ant-select-item.ant-select-item-option-selected {
  color: var(--primary-color);
  font-weight: normal;
  background-color: var(--color-surface);
}

.ant-select-dropdown .ant-select-item:hover {
  background-color: var(--ds-gray-8);
}

.ant-select-dropdown
  .ant-select-item.ant-select-item-option-selected:has(.ant-checkbox-wrapper) {
  color: var(--primary-color);
  background-color: var(--color-surface) !important;
}

::-webkit-scrollbar {
  width: 4px;
  background: var(--scrollbar-track-bg);
}

::-webkit-scrollbar-thumb {
  border-radius: 4px;
  background: var(--scrollbar-thumb-bg);
}

::-webkit-scrollbar-thumb:hover {
  background: var(--scrollbar-thumb-hover-bg);
}

/**
 * Copyright (c) 2016 Connor Atherton
 *
 * All animations must live in their own file
 * in the animations directory and be included
 * here.
 *
 */
/**
 * Styles shared by multiple animations
 */
/**
 * Dots
 */
@-webkit-keyframes scale {
  0% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  45% {
    opacity: 0.7;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  80% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
.ball-pulse > div:nth-child(1) {
  -webkit-animation: scale 0.75s -0.24s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  animation: scale 0.75s -0.24s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
}
.ball-pulse > div:nth-child(2) {
  -webkit-animation: scale 0.75s -0.12s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  animation: scale 0.75s -0.12s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
}
.ball-pulse > div:nth-child(3) {
  -webkit-animation: scale 0.75s 0s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  animation: scale 0.75s 0s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
}
.ball-pulse > div {
  width: 15px;
  height: 15px;
  margin: 2px;
  border-radius: 100%;
  display: inline-block;
  background-color: var(--white);
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@-webkit-keyframes ball-pulse-sync {
  33% {
    -webkit-transform: translateY(10px);
    transform: translateY(10px);
  }
  66% {
    -webkit-transform: translateY(-10px);
    transform: translateY(-10px);
  }
  100% {
    -webkit-transform: translateY(0);
    transform: translateY(0);
  }
}
.ball-pulse-sync > div:nth-child(1) {
  -webkit-animation: ball-pulse-sync 0.6s -0.14s infinite ease-in-out;
  animation: ball-pulse-sync 0.6s -0.14s infinite ease-in-out;
}
.ball-pulse-sync > div:nth-child(2) {
  -webkit-animation: ball-pulse-sync 0.6s -0.07s infinite ease-in-out;
  animation: ball-pulse-sync 0.6s -0.07s infinite ease-in-out;
}
.ball-pulse-sync > div:nth-child(3) {
  -webkit-animation: ball-pulse-sync 0.6s 0s infinite ease-in-out;
  animation: ball-pulse-sync 0.6s 0s infinite ease-in-out;
}
.ball-pulse-sync > div {
  width: 15px;
  height: 15px;
  margin: 2px;
  border-radius: 100%;
  display: inline-block;
  background-color: var(--white);
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@-webkit-keyframes ball-scale {
  0% {
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
.ball-scale > div {
  width: 15px;
  width: 60px;
  height: 15px;
  height: 60px;
  margin: 2px;
  border-radius: 100%;
  display: inline-block;
  background-color: var(--white);
  -webkit-animation: ball-scale 1s 0s ease-in-out infinite;
  animation: ball-scale 1s 0s ease-in-out infinite;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.ball-scale > div {
  width: 15px;
  width: 60px;
  height: 15px;
  height: 60px;
  margin: 2px;
  border-radius: 100%;
  display: inline-block;
  background-color: var(--white);
  -webkit-animation: ball-scale 1s 0s ease-in-out infinite;
  animation: ball-scale 1s 0s ease-in-out infinite;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.ball-scale-random {
  width: 37px;
  height: 40px;
}
.ball-scale-random > div {
  width: 15px;
  width: 30px;
  height: 15px;
  height: 30px;
  margin: 2px;
  border-radius: 100%;
  position: absolute;
  display: inline-block;
  background-color: var(--white);
  -webkit-animation: ball-scale 1s 0s ease-in-out infinite;
  animation: ball-scale 1s 0s ease-in-out infinite;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.ball-scale-random > div:nth-child(1) {
  margin-left: -7px;
  -webkit-animation: ball-scale 1s 0.2s ease-in-out infinite;
  animation: ball-scale 1s 0.2s ease-in-out infinite;
}
.ball-scale-random > div:nth-child(3) {
  margin-top: 9px;
  margin-left: -2px;
  -webkit-animation: ball-scale 1s 0.5s ease-in-out infinite;
  animation: ball-scale 1s 0.5s ease-in-out infinite;
}
@-webkit-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
.ball-rotate {
  position: relative;
}
.ball-rotate > div {
  width: 15px;
  height: 15px;
  margin: 2px;
  border-radius: 100%;
  position: relative;
  background-color: var(--white);
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.ball-rotate > div:first-child {
  -webkit-animation: rotate 1s 0s cubic-bezier(0.7, -0.13, 0.22, 0.86) infinite;
  animation: rotate 1s 0s cubic-bezier(0.7, -0.13, 0.22, 0.86) infinite;
}
.ball-rotate > div:before,
.ball-rotate > div:after {
  width: 15px;
  height: 15px;
  margin: 2px;
  border-radius: 100%;
  content: '';
  position: absolute;
  background-color: var(--white);
  opacity: 0.8;
}
.ball-rotate > div:before {
  top: 0px;
  left: -28px;
}
.ball-rotate > div:after {
  top: 0px;
  left: 25px;
}
.ball-clip-rotate > div {
  width: 15px;
  width: 15px;
  height: 15px;
  height: 15px;
  margin: 2px;
  border: 2px solid var(--primary-color);
  border-bottom-color: transparent;
  border-radius: 100%;
  display: inline-block;
  background: transparent !important;
  background-color: var(--primary-color);
  -webkit-animation: justRotate 0.75s 0s linear infinite;
  animation: justRotate 0.75s 0s linear infinite;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.ball-clip-rotate-pulse {
  position: relative;
  -webkit-transform: translateY(-15px);
  transform: translateY(-15px);
}
.ball-clip-rotate-pulse > div {
  border-radius: 100%;
  position: absolute;
  top: 0px;
  left: 0px;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.ball-clip-rotate-pulse > div:first-child {
  width: 16px;
  height: 16px;
  top: 7px;
  left: -7px;
  background: var(--white);
  -webkit-animation: scale 1s 0s cubic-bezier(0.09, 0.57, 0.49, 0.9) infinite;
  animation: scale 1s 0s cubic-bezier(0.09, 0.57, 0.49, 0.9) infinite;
}
.ball-clip-rotate-pulse > div:last-child {
  width: 30px;
  height: 30px;
  border: 2px solid var(--white);
  border: 2px solid;
  border-color: var(--white) transparent var(--white) transparent;
  position: absolute;
  top: -2px;
  left: -16px;
  background: transparent;
  -webkit-animation: rotate 1s 0s cubic-bezier(0.09, 0.57, 0.49, 0.9) infinite;
  animation: rotate 1s 0s cubic-bezier(0.09, 0.57, 0.49, 0.9) infinite;
  -webkit-animation-duration: 1s;
  animation-duration: 1s;
}
.ball-clip-rotate-multiple {
  position: relative;
}
.ball-clip-rotate-multiple > div {
  width: 35px;
  height: 35px;
  border: 2px solid var(--white);
  border-top-color: transparent;
  border-bottom-color: transparent;
  border-radius: 100%;
  position: absolute;
  top: -20px;
  left: -20px;
  -webkit-animation: rotate 1s 0s ease-in-out infinite;
  animation: rotate 1s 0s ease-in-out infinite;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.ball-clip-rotate-multiple > div:last-child {
  width: 15px;
  height: 15px;
  border-color: var(--white) transparent var(--white) transparent;
  top: -10px;
  left: -10px;
  display: inline-block;
  -webkit-animation-duration: 0.5s;
  animation-duration: 0.5s;
  -webkit-animation-direction: reverse;
  animation-direction: reverse;
}
@-webkit-keyframes ball-scale-ripple {
  0% {
    opacity: 1;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  70% {
    opacity: 0.7;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  100% {
    opacity: 0;
  }
}
.ball-scale-ripple > div {
  width: 50px;
  height: 50px;
  border: 2px solid var(--white);
  border-radius: 100%;
  -webkit-animation: ball-scale-ripple 1s 0s infinite cubic-bezier(0.21, 0.53, 0.56, 0.8);
  animation: ball-scale-ripple 1s 0s infinite cubic-bezier(0.21, 0.53, 0.56, 0.8);
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@-webkit-keyframes ball-scale-ripple-multiple {
  0% {
    opacity: 1;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  70% {
    opacity: 0.7;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  100% {
    opacity: 0;
  }
}
.ball-scale-ripple-multiple {
  position: relative;
  -webkit-transform: translateY(-25px);
  transform: translateY(-25px);
}
.ball-scale-ripple-multiple > div:nth-child(0) {
  -webkit-animation-delay: -0.8s;
  animation-delay: -0.8s;
}
.ball-scale-ripple-multiple > div:nth-child(1) {
  -webkit-animation-delay: -0.6s;
  animation-delay: -0.6s;
}
.ball-scale-ripple-multiple > div:nth-child(2) {
  -webkit-animation-delay: -0.4s;
  animation-delay: -0.4s;
}
.ball-scale-ripple-multiple > div:nth-child(3) {
  -webkit-animation-delay: -0.2s;
  animation-delay: -0.2s;
}
.ball-scale-ripple-multiple > div {
  width: 50px;
  height: 50px;
  border: 2px solid var(--white);
  border-radius: 100%;
  position: absolute;
  top: -2px;
  left: -26px;
  -webkit-animation: ball-scale-ripple-multiple 1.25s 0s infinite cubic-bezier(0.21, 0.53, 0.56, 0.8);
  animation: ball-scale-ripple-multiple 1.25s 0s infinite cubic-bezier(0.21, 0.53, 0.56, 0.8);
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@-webkit-keyframes ball-beat {
  50% {
    opacity: 0.2;
    -webkit-transform: scale(0.75);
    transform: scale(0.75);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
.ball-beat > div {
  width: 15px;
  height: 15px;
  margin: 2px;
  border-radius: 100%;
  display: inline-block;
  background-color: var(--primary-color);
  -webkit-animation: ball-beat 0.7s 0s infinite linear;
  animation: ball-beat 0.7s 0s infinite linear;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.ball-beat > div:nth-child(2n-1) {
  -webkit-animation-delay: -0.35s !important;
  animation-delay: -0.35s !important;
}
@-webkit-keyframes ball-scale-multiple {
  0% {
    opacity: 0;
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  5% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
.ball-scale-multiple {
  position: relative;
  -webkit-transform: translateY(-30px);
  transform: translateY(-30px);
}
.ball-scale-multiple > div:nth-child(2) {
  -webkit-animation-delay: -0.4s;
  animation-delay: -0.4s;
}
.ball-scale-multiple > div:nth-child(3) {
  -webkit-animation-delay: -0.2s;
  animation-delay: -0.2s;
}
.ball-scale-multiple > div {
  width: 15px;
  width: 60px;
  height: 15px;
  height: 60px;
  margin: 2px;
  margin: 0;
  border-radius: 100%;
  position: absolute;
  top: 0px;
  left: -30px;
  background-color: var(--primary-color);
  opacity: 0;
  -webkit-animation: ball-scale-multiple 1s 0s linear infinite;
  animation: ball-scale-multiple 1s 0s linear infinite;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@-webkit-keyframes ball-triangle-path-1 {
  33% {
    -webkit-transform: translate(25px, -50px);
    transform: translate(25px, -50px);
  }
  66% {
    -webkit-transform: translate(50px, 0px);
    transform: translate(50px, 0px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@-webkit-keyframes ball-triangle-path-2 {
  33% {
    -webkit-transform: translate(25px, 50px);
    transform: translate(25px, 50px);
  }
  66% {
    -webkit-transform: translate(-25px, 50px);
    transform: translate(-25px, 50px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@-webkit-keyframes ball-triangle-path-3 {
  33% {
    -webkit-transform: translate(-50px, 0px);
    transform: translate(-50px, 0px);
  }
  66% {
    -webkit-transform: translate(-25px, -50px);
    transform: translate(-25px, -50px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
.ball-triangle-path {
  position: relative;
  -webkit-transform: translate(-29.994px, -37.50938px);
  transform: translate(-29.994px, -37.50938px);
}
.ball-triangle-path > div:nth-child(1) {
  -webkit-animation-name: ball-triangle-path-1;
  animation-name: ball-triangle-path-1;
  -webkit-animation-duration: 2s;
  animation-duration: 2s;
  -webkit-animation-timing-function: ease-in-out;
  animation-timing-function: ease-in-out;
  -webkit-animation-delay: 0;
  animation-delay: 0;
  -webkit-animation-iteration-count: infinite;
  animation-iteration-count: infinite;
}
.ball-triangle-path > div:nth-child(2) {
  -webkit-animation-name: ball-triangle-path-2;
  animation-name: ball-triangle-path-2;
  -webkit-animation-duration: 2s;
  animation-duration: 2s;
  -webkit-animation-timing-function: ease-in-out;
  animation-timing-function: ease-in-out;
  -webkit-animation-delay: 0;
  animation-delay: 0;
  -webkit-animation-iteration-count: infinite;
  animation-iteration-count: infinite;
}
.ball-triangle-path > div:nth-child(3) {
  -webkit-animation-name: ball-triangle-path-3;
  animation-name: ball-triangle-path-3;
  -webkit-animation-duration: 2s;
  animation-duration: 2s;
  -webkit-animation-timing-function: ease-in-out;
  animation-timing-function: ease-in-out;
  -webkit-animation-delay: 0;
  animation-delay: 0;
  -webkit-animation-iteration-count: infinite;
  animation-iteration-count: infinite;
}
.ball-triangle-path > div {
  width: 10px;
  height: 10px;
  border: 1px solid var(--white);
  border-radius: 100%;
  position: absolute;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.ball-triangle-path > div:nth-of-type(1) {
  top: 50px;
}
.ball-triangle-path > div:nth-of-type(2) {
  left: 25px;
}
.ball-triangle-path > div:nth-of-type(3) {
  top: 50px;
  left: 50px;
}
@-webkit-keyframes ball-pulse-rise-even {
  0% {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }
  25% {
    -webkit-transform: translateY(-30px);
    transform: translateY(-30px);
  }
  50% {
    -webkit-transform: scale(0.4);
    transform: scale(0.4);
  }
  75% {
    -webkit-transform: translateY(30px);
    transform: translateY(30px);
  }
  100% {
    -webkit-transform: translateY(0);
    -webkit-transform: scale(1);
    transform: translateY(0);
    transform: scale(1);
  }
}
@-webkit-keyframes ball-pulse-rise-odd {
  0% {
    -webkit-transform: scale(0.4);
    transform: scale(0.4);
  }
  25% {
    -webkit-transform: translateY(30px);
    transform: translateY(30px);
  }
  50% {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }
  75% {
    -webkit-transform: translateY(-30px);
    transform: translateY(-30px);
  }
  100% {
    -webkit-transform: translateY(0);
    -webkit-transform: scale(0.75);
    transform: translateY(0);
    transform: scale(0.75);
  }
}
.ball-pulse-rise > div {
  width: 15px;
  height: 15px;
  margin: 2px;
  border-radius: 100%;
  display: inline-block;
  background-color: var(--white);
  -webkit-animation-duration: 1s;
  animation-duration: 1s;
  -webkit-animation-timing-function: cubic-bezier(0.15, 0.46, 0.9, 0.6);
  animation-timing-function: cubic-bezier(0.15, 0.46, 0.9, 0.6);
  -webkit-animation-delay: 0;
  animation-delay: 0;
  -webkit-animation-iteration-count: infinite;
  animation-iteration-count: infinite;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.ball-pulse-rise > div:nth-child(2n) {
  -webkit-animation-name: ball-pulse-rise-even;
  animation-name: ball-pulse-rise-even;
}
.ball-pulse-rise > div:nth-child(2n-1) {
  -webkit-animation-name: ball-pulse-rise-odd;
  animation-name: ball-pulse-rise-odd;
}
@-webkit-keyframes ball-grid-beat {
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}
.ball-grid-beat {
  width: 57px;
}
.ball-grid-beat > div:nth-child(1) {
  -webkit-animation-duration: 1.45s;
  animation-duration: 1.45s;
  -webkit-animation-delay: 0.15s;
  animation-delay: 0.15s;
}
.ball-grid-beat > div:nth-child(2) {
  -webkit-animation-duration: 0.97s;
  animation-duration: 0.97s;
  -webkit-animation-delay: -0.02s;
  animation-delay: -0.02s;
}
.ball-grid-beat > div:nth-child(3) {
  -webkit-animation-duration: 1.23s;
  animation-duration: 1.23s;
  -webkit-animation-delay: 0.66s;
  animation-delay: 0.66s;
}
.ball-grid-beat > div:nth-child(4) {
  -webkit-animation-duration: 1.24s;
  animation-duration: 1.24s;
  -webkit-animation-delay: 0.64s;
  animation-delay: 0.64s;
}
.ball-grid-beat > div:nth-child(5) {
  -webkit-animation-duration: 1.13s;
  animation-duration: 1.13s;
  -webkit-animation-delay: -0.19s;
  animation-delay: -0.19s;
}
.ball-grid-beat > div:nth-child(6) {
  -webkit-animation-duration: 1.42s;
  animation-duration: 1.42s;
  -webkit-animation-delay: 0.69s;
  animation-delay: 0.69s;
}
.ball-grid-beat > div:nth-child(7) {
  -webkit-animation-duration: 1.14s;
  animation-duration: 1.14s;
  -webkit-animation-delay: 0.58s;
  animation-delay: 0.58s;
}
.ball-grid-beat > div:nth-child(8) {
  -webkit-animation-duration: 1.17s;
  animation-duration: 1.17s;
  -webkit-animation-delay: 0.21s;
  animation-delay: 0.21s;
}
.ball-grid-beat > div:nth-child(9) {
  -webkit-animation-duration: 0.65s;
  animation-duration: 0.65s;
  -webkit-animation-delay: -0.18s;
  animation-delay: -0.18s;
}
.ball-grid-beat > div {
  width: 15px;
  height: 15px;
  margin: 2px;
  border-radius: 100%;
  display: inline-block;
  float: left;
  background-color: var(--white);
  -webkit-animation-name: ball-grid-beat;
  animation-name: ball-grid-beat;
  -webkit-animation-delay: 0;
  animation-delay: 0;
  -webkit-animation-iteration-count: infinite;
  animation-iteration-count: infinite;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@-webkit-keyframes ball-grid-pulse {
  0% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    -webkit-transform: scale(0.5);
    transform: scale(0.5);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
.ball-grid-pulse {
  width: 57px;
}
.ball-grid-pulse > div:nth-child(1) {
  -webkit-animation-duration: 0.9s;
  animation-duration: 0.9s;
  -webkit-animation-delay: 0.22s;
  animation-delay: 0.22s;
}
.ball-grid-pulse > div:nth-child(2) {
  -webkit-animation-duration: 1s;
  animation-duration: 1s;
  -webkit-animation-delay: 0.64s;
  animation-delay: 0.64s;
}
.ball-grid-pulse > div:nth-child(3) {
  -webkit-animation-duration: 0.63s;
  animation-duration: 0.63s;
  -webkit-animation-delay: -0.15s;
  animation-delay: -0.15s;
}
.ball-grid-pulse > div:nth-child(4) {
  -webkit-animation-duration: 1.24s;
  animation-duration: 1.24s;
  -webkit-animation-delay: -0.03s;
  animation-delay: -0.03s;
}
.ball-grid-pulse > div:nth-child(5) {
  -webkit-animation-duration: 1.37s;
  animation-duration: 1.37s;
  -webkit-animation-delay: 0.08s;
  animation-delay: 0.08s;
}
.ball-grid-pulse > div:nth-child(6) {
  -webkit-animation-duration: 1.55s;
  animation-duration: 1.55s;
  -webkit-animation-delay: 0.43s;
  animation-delay: 0.43s;
}
.ball-grid-pulse > div:nth-child(7) {
  -webkit-animation-duration: 0.7s;
  animation-duration: 0.7s;
  -webkit-animation-delay: 0.05s;
  animation-delay: 0.05s;
}
.ball-grid-pulse > div:nth-child(8) {
  -webkit-animation-duration: 0.97s;
  animation-duration: 0.97s;
  -webkit-animation-delay: 0.05s;
  animation-delay: 0.05s;
}
.ball-grid-pulse > div:nth-child(9) {
  -webkit-animation-duration: 0.63s;
  animation-duration: 0.63s;
  -webkit-animation-delay: 0.3s;
  animation-delay: 0.3s;
}
.ball-grid-pulse > div {
  width: 15px;
  height: 15px;
  margin: 2px;
  border-radius: 100%;
  display: inline-block;
  float: left;
  background-color: var(--white);
  -webkit-animation-name: ball-grid-pulse;
  animation-name: ball-grid-pulse;
  -webkit-animation-delay: 0;
  animation-delay: 0;
  -webkit-animation-iteration-count: infinite;
  animation-iteration-count: infinite;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@-webkit-keyframes ball-spin-loader {
  75% {
    opacity: 0.2;
  }
  100% {
    opacity: 1;
  }
}
.ball-spin-loader {
  position: relative;
}
.ball-spin-loader > span:nth-child(1) {
  top: 45px;
  left: 0;
  -webkit-animation: ball-spin-loader 2s 0.9s infinite linear;
  animation: ball-spin-loader 2s 0.9s infinite linear;
}
.ball-spin-loader > span:nth-child(2) {
  top: 30.68182px;
  left: 30.68182px;
  -webkit-animation: ball-spin-loader 2s 1.8s infinite linear;
  animation: ball-spin-loader 2s 1.8s infinite linear;
}
.ball-spin-loader > span:nth-child(3) {
  top: 0;
  left: 45px;
  -webkit-animation: ball-spin-loader 2s 2.7s infinite linear;
  animation: ball-spin-loader 2s 2.7s infinite linear;
}
.ball-spin-loader > span:nth-child(4) {
  top: -30.68182px;
  left: 30.68182px;
  -webkit-animation: ball-spin-loader 2s 3.6s infinite linear;
  animation: ball-spin-loader 2s 3.6s infinite linear;
}
.ball-spin-loader > span:nth-child(5) {
  top: -45px;
  left: 0;
  -webkit-animation: ball-spin-loader 2s 4.5s infinite linear;
  animation: ball-spin-loader 2s 4.5s infinite linear;
}
.ball-spin-loader > span:nth-child(6) {
  top: -30.68182px;
  left: -30.68182px;
  -webkit-animation: ball-spin-loader 2s 5.4s infinite linear;
  animation: ball-spin-loader 2s 5.4s infinite linear;
}
.ball-spin-loader > span:nth-child(7) {
  top: 0;
  left: -45px;
  -webkit-animation: ball-spin-loader 2s 6.3s infinite linear;
  animation: ball-spin-loader 2s 6.3s infinite linear;
}
.ball-spin-loader > span:nth-child(8) {
  top: 30.68182px;
  left: -30.68182px;
  -webkit-animation: ball-spin-loader 2s 7.2s infinite linear;
  animation: ball-spin-loader 2s 7.2s infinite linear;
}
.ball-spin-loader > div {
  width: 15px;
  height: 15px;
  border-radius: 100%;
  position: absolute;
  background: #008000;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@-webkit-keyframes ball-zig {
  33% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  66% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-webkit-keyframes ball-zag {
  33% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  66% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
.ball-zig-zag {
  position: relative;
  -webkit-transform: translate(-15px, -15px);
  transform: translate(-15px, -15px);
}
.ball-zig-zag > div {
  width: 15px;
  height: 15px;
  margin: 2px;
  margin-left: 15px;
  border-radius: 100%;
  position: absolute;
  top: 4px;
  left: -7px;
  background-color: var(--white);
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.ball-zig-zag > div:first-child {
  -webkit-animation: ball-zig 0.7s 0s infinite linear;
  animation: ball-zig 0.7s 0s infinite linear;
}
.ball-zig-zag > div:last-child {
  -webkit-animation: ball-zag 0.7s 0s infinite linear;
  animation: ball-zag 0.7s 0s infinite linear;
}
@-webkit-keyframes ball-zig-deflect {
  17% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  34% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  50% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
  67% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  84% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-webkit-keyframes ball-zag-deflect {
  17% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  34% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  50% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
  67% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  84% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
.ball-zig-zag-deflect {
  position: relative;
  -webkit-transform: translate(-15px, -15px);
  transform: translate(-15px, -15px);
}
.ball-zig-zag-deflect > div {
  width: 15px;
  height: 15px;
  margin: 2px;
  margin-left: 15px;
  border-radius: 100%;
  position: absolute;
  top: 4px;
  left: -7px;
  background-color: var(--white);
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.ball-zig-zag-deflect > div:first-child {
  -webkit-animation: ball-zig-deflect 1.5s 0s infinite linear;
  animation: ball-zig-deflect 1.5s 0s infinite linear;
}
.ball-zig-zag-deflect > div:last-child {
  -webkit-animation: ball-zag-deflect 1.5s 0s infinite linear;
  animation: ball-zag-deflect 1.5s 0s infinite linear;
}
/**
 * Lines
 */
@-webkit-keyframes line-scale {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  50% {
    -webkit-transform: scaley(0.4);
    transform: scaley(0.4);
  }
  100% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
.line-scale > div:nth-child(1) {
  -webkit-animation: line-scale 1s -0.4s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  animation: line-scale 1s -0.4s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
}
.line-scale > div:nth-child(2) {
  -webkit-animation: line-scale 1s -0.3s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  animation: line-scale 1s -0.3s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
}
.line-scale > div:nth-child(3) {
  -webkit-animation: line-scale 1s -0.2s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  animation: line-scale 1s -0.2s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
}
.line-scale > div:nth-child(4) {
  -webkit-animation: line-scale 1s -0.1s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  animation: line-scale 1s -0.1s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
}
.line-scale > div:nth-child(5) {
  -webkit-animation: line-scale 1s 0s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
  animation: line-scale 1s 0s infinite cubic-bezier(0.2, 0.68, 0.18, 1.08);
}
.line-scale > div {
  width: 4px;
  height: 35px;
  margin: 2px;
  border-radius: 2px;
  display: inline-block;
  background-color: var(--white);
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@-webkit-keyframes line-scale-party {
  0% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  50% {
    -webkit-transform: scale(0.5);
    transform: scale(0.5);
  }
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
.line-scale-party > div:nth-child(1) {
  -webkit-animation-duration: 0.54s;
  animation-duration: 0.54s;
  -webkit-animation-delay: 0.48s;
  animation-delay: 0.48s;
}
.line-scale-party > div:nth-child(2) {
  -webkit-animation-duration: 1.15s;
  animation-duration: 1.15s;
  -webkit-animation-delay: -0.15s;
  animation-delay: -0.15s;
}
.line-scale-party > div:nth-child(3) {
  -webkit-animation-duration: 0.77s;
  animation-duration: 0.77s;
  -webkit-animation-delay: 0.04s;
  animation-delay: 0.04s;
}
.line-scale-party > div:nth-child(4) {
  -webkit-animation-duration: 0.61s;
  animation-duration: 0.61s;
  -webkit-animation-delay: -0.12s;
  animation-delay: -0.12s;
}
.line-scale-party > div {
  width: 4px;
  height: 35px;
  margin: 2px;
  border-radius: 2px;
  display: inline-block;
  background-color: var(--white);
  -webkit-animation-name: line-scale-party;
  animation-name: line-scale-party;
  -webkit-animation-delay: 0;
  animation-delay: 0;
  -webkit-animation-iteration-count: infinite;
  animation-iteration-count: infinite;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@-webkit-keyframes line-scale-pulse-out {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  50% {
    -webkit-transform: scaley(0.4);
    transform: scaley(0.4);
  }
  100% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
.line-scale-pulse-out > div {
  width: 4px;
  height: 35px;
  margin: 2px;
  border-radius: 2px;
  display: inline-block;
  background-color: var(--white);
  -webkit-animation: line-scale-pulse-out 0.9s -0.6s infinite cubic-bezier(0.85, 0.25, 0.37, 0.85);
  animation: line-scale-pulse-out 0.9s -0.6s infinite cubic-bezier(0.85, 0.25, 0.37, 0.85);
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.line-scale-pulse-out > div:nth-child(2),
.line-scale-pulse-out > div:nth-child(4) {
  -webkit-animation-delay: -0.4s !important;
  animation-delay: -0.4s !important;
}
.line-scale-pulse-out > div:nth-child(1),
.line-scale-pulse-out > div:nth-child(5) {
  -webkit-animation-delay: -0.2s !important;
  animation-delay: -0.2s !important;
}
@-webkit-keyframes line-scale-pulse-out-rapid {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  80% {
    -webkit-transform: scaley(0.3);
    transform: scaley(0.3);
  }
  90% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
.line-scale-pulse-out-rapid > div {
  width: 4px;
  height: 35px;
  margin: 2px;
  border-radius: 2px;
  display: inline-block;
  vertical-align: middle;
  background-color: var(--white);
  -webkit-animation: line-scale-pulse-out-rapid 0.9s -0.5s infinite cubic-bezier(0.11, 0.49, 0.38, 0.78);
  animation: line-scale-pulse-out-rapid 0.9s -0.5s infinite cubic-bezier(0.11, 0.49, 0.38, 0.78);
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.line-scale-pulse-out-rapid > div:nth-child(2),
.line-scale-pulse-out-rapid > div:nth-child(4) {
  -webkit-animation-delay: -0.25s !important;
  animation-delay: -0.25s !important;
}
.line-scale-pulse-out-rapid > div:nth-child(1),
.line-scale-pulse-out-rapid > div:nth-child(5) {
  -webkit-animation-delay: 0s !important;
  animation-delay: 0s !important;
}
@-webkit-keyframes line-spin-fade-loader {
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 1;
  }
}
.line-spin-fade-loader {
  position: relative;
  top: -10px;
  left: -4px;
}
.line-spin-fade-loader > div:nth-child(1) {
  top: 20px;
  left: 0;
  -webkit-animation: line-spin-fade-loader 1.2s -0.84s infinite ease-in-out;
  animation: line-spin-fade-loader 1.2s -0.84s infinite ease-in-out;
}
.line-spin-fade-loader > div:nth-child(2) {
  top: 13.63636px;
  left: 13.63636px;
  -webkit-transform: rotate(-45deg);
  transform: rotate(-45deg);
  -webkit-animation: line-spin-fade-loader 1.2s -0.72s infinite ease-in-out;
  animation: line-spin-fade-loader 1.2s -0.72s infinite ease-in-out;
}
.line-spin-fade-loader > div:nth-child(3) {
  top: 0;
  left: 20px;
  -webkit-transform: rotate(90deg);
  transform: rotate(90deg);
  -webkit-animation: line-spin-fade-loader 1.2s -0.6s infinite ease-in-out;
  animation: line-spin-fade-loader 1.2s -0.6s infinite ease-in-out;
}
.line-spin-fade-loader > div:nth-child(4) {
  top: -13.63636px;
  left: 13.63636px;
  -webkit-transform: rotate(45deg);
  transform: rotate(45deg);
  -webkit-animation: line-spin-fade-loader 1.2s -0.48s infinite ease-in-out;
  animation: line-spin-fade-loader 1.2s -0.48s infinite ease-in-out;
}
.line-spin-fade-loader > div:nth-child(5) {
  top: -20px;
  left: 0;
  -webkit-animation: line-spin-fade-loader 1.2s -0.36s infinite ease-in-out;
  animation: line-spin-fade-loader 1.2s -0.36s infinite ease-in-out;
}
.line-spin-fade-loader > div:nth-child(6) {
  top: -13.63636px;
  left: -13.63636px;
  -webkit-transform: rotate(-45deg);
  transform: rotate(-45deg);
  -webkit-animation: line-spin-fade-loader 1.2s -0.24s infinite ease-in-out;
  animation: line-spin-fade-loader 1.2s -0.24s infinite ease-in-out;
}
.line-spin-fade-loader > div:nth-child(7) {
  top: 0;
  left: -20px;
  -webkit-transform: rotate(90deg);
  transform: rotate(90deg);
  -webkit-animation: line-spin-fade-loader 1.2s -0.12s infinite ease-in-out;
  animation: line-spin-fade-loader 1.2s -0.12s infinite ease-in-out;
}
.line-spin-fade-loader > div:nth-child(8) {
  top: 13.63636px;
  left: -13.63636px;
  -webkit-transform: rotate(45deg);
  transform: rotate(45deg);
  -webkit-animation: line-spin-fade-loader 1.2s 0s infinite ease-in-out;
  animation: line-spin-fade-loader 1.2s 0s infinite ease-in-out;
}
.line-spin-fade-loader > div {
  width: 4px;
  width: 5px;
  height: 35px;
  height: 15px;
  margin: 2px;
  border-radius: 2px;
  position: absolute;
  background-color: var(--white);
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
/**
 * Misc
 */
@-webkit-keyframes triangle-skew-spin {
  25% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(0);
    transform: perspective(100px) rotateX(180deg) rotateY(0);
  }
  50% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(180deg);
    transform: perspective(100px) rotateX(180deg) rotateY(180deg);
  }
  75% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(180deg);
    transform: perspective(100px) rotateX(0) rotateY(180deg);
  }
  100% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(0);
    transform: perspective(100px) rotateX(0) rotateY(0);
  }
}
.triangle-skew-spin > div {
  width: 0;
  height: 0;
  border-right: 20px solid transparent;
  border-bottom: 20px solid var(--white);
  border-left: 20px solid transparent;
  -webkit-animation: triangle-skew-spin 3s 0s cubic-bezier(0.09, 0.57, 0.49, 0.9) infinite;
  animation: triangle-skew-spin 3s 0s cubic-bezier(0.09, 0.57, 0.49, 0.9) infinite;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@-webkit-keyframes square-spin {
  25% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(0);
    transform: perspective(100px) rotateX(180deg) rotateY(0);
  }
  50% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(180deg);
    transform: perspective(100px) rotateX(180deg) rotateY(180deg);
  }
  75% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(180deg);
    transform: perspective(100px) rotateX(0) rotateY(180deg);
  }
  100% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(0);
    transform: perspective(100px) rotateX(0) rotateY(0);
  }
}
.square-spin > div {
  width: 50px;
  height: 50px;
  background: var(--white);
  -webkit-animation: square-spin 3s 0s cubic-bezier(0.09, 0.57, 0.49, 0.9) infinite;
  animation: square-spin 3s 0s cubic-bezier(0.09, 0.57, 0.49, 0.9) infinite;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
@-webkit-keyframes rotate_pacman_half_up {
  0% {
    -webkit-transform: rotate(270deg);
    transform: rotate(270deg);
  }
  50% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
  100% {
    -webkit-transform: rotate(270deg);
    transform: rotate(270deg);
  }
}
@-webkit-keyframes rotate_pacman_half_down {
  0% {
    -webkit-transform: rotate(90deg);
    transform: rotate(90deg);
  }
  50% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(90deg);
    transform: rotate(90deg);
  }
}
@-webkit-keyframes pacman-balls {
  75% {
    opacity: 0.7;
  }
  100% {
    -webkit-transform: translate(-100px, -6.25px);
    transform: translate(-100px, -6.25px);
  }
}
.pacman {
  position: relative;
}
.pacman > div:nth-child(2) {
  -webkit-animation: pacman-balls 1s -0.99s infinite linear;
  animation: pacman-balls 1s -0.99s infinite linear;
}
.pacman > div:nth-child(3) {
  -webkit-animation: pacman-balls 1s -0.66s infinite linear;
  animation: pacman-balls 1s -0.66s infinite linear;
}
.pacman > div:nth-child(4) {
  -webkit-animation: pacman-balls 1s -0.33s infinite linear;
  animation: pacman-balls 1s -0.33s infinite linear;
}
.pacman > div:nth-child(5) {
  -webkit-animation: pacman-balls 1s 0s infinite linear;
  animation: pacman-balls 1s 0s infinite linear;
}
.pacman > div:first-of-type {
  width: 0px;
  height: 0px;
  border-top: 25px solid var(--white);
  border-right: 25px solid transparent;
  border-bottom: 25px solid var(--white);
  border-left: 25px solid var(--white);
  border-radius: 25px;
  position: relative;
  left: -30px;
  -webkit-animation: rotate_pacman_half_up 0.5s 0s infinite;
  animation: rotate_pacman_half_up 0.5s 0s infinite;
}
.pacman > div:nth-child(2) {
  width: 0px;
  height: 0px;
  margin-top: -50px;
  border-top: 25px solid var(--white);
  border-right: 25px solid transparent;
  border-bottom: 25px solid var(--white);
  border-left: 25px solid var(--white);
  border-radius: 25px;
  position: relative;
  left: -30px;
  -webkit-animation: rotate_pacman_half_down 0.5s 0s infinite;
  animation: rotate_pacman_half_down 0.5s 0s infinite;
}
.pacman > div:nth-child(3),
.pacman > div:nth-child(4),
.pacman > div:nth-child(5),
.pacman > div:nth-child(6) {
  width: 15px;
  width: 10px;
  height: 15px;
  height: 10px;
  margin: 2px;
  border-radius: 100%;
  position: absolute;
  top: 25px;
  left: 70px;
  background-color: var(--white);
  -webkit-transform: translate(0, -6.25px);
  transform: translate(0, -6.25px);
}
@-webkit-keyframes cube-transition {
  25% {
    -webkit-transform: translateX(50px) scale(0.5) rotate(-90deg);
    transform: translateX(50px) scale(0.5) rotate(-90deg);
  }
  50% {
    -webkit-transform: translate(50px, 50px) rotate(-180deg);
    transform: translate(50px, 50px) rotate(-180deg);
  }
  75% {
    -webkit-transform: translateY(50px) scale(0.5) rotate(-270deg);
    transform: translateY(50px) scale(0.5) rotate(-270deg);
  }
  100% {
    -webkit-transform: rotate(-360deg);
    transform: rotate(-360deg);
  }
}
.cube-transition {
  position: relative;
  -webkit-transform: translate(-25px, -25px);
  transform: translate(-25px, -25px);
}
.cube-transition > div {
  width: 10px;
  height: 10px;
  position: absolute;
  top: -5px;
  left: -5px;
  background-color: var(--white);
  -webkit-animation: cube-transition 1.6s 0s infinite ease-in-out;
  animation: cube-transition 1.6s 0s infinite ease-in-out;
  -webkit-animation-fill-mode: both;
  animation-fill-mode: both;
}
.cube-transition > div:last-child {
  -webkit-animation-delay: -0.8s;
  animation-delay: -0.8s;
}
@-webkit-keyframes spin-rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
.semi-circle-spin {
  width: 35px;
  height: 35px;
  position: relative;
  overflow: hidden;
}
.semi-circle-spin > div {
  width: 100%;
  height: 100%;
  border-width: 0px;
  border-radius: 100%;
  position: absolute;
  background-image: linear-gradient(transparent 0%, transparent 70%, var(--primary-color) 30%, var(--primary-color) 100%);
  -webkit-animation: spin-rotate 0.8s 0s infinite linear;
  animation: spin-rotate 0.8s 0s infinite linear;
}
@-moz-keyframes scale {
  0% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  45% {
    opacity: 0.7;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  80% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-webkit-keyframes scale {
  0% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  45% {
    opacity: 0.7;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  80% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-o-keyframes scale {
  0% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  45% {
    opacity: 0.7;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  80% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@keyframes scale {
  0% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  45% {
    opacity: 0.7;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  80% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-moz-keyframes ball-pulse-sync {
  33% {
    -webkit-transform: translateY(10px);
    transform: translateY(10px);
  }
  66% {
    -webkit-transform: translateY(-10px);
    transform: translateY(-10px);
  }
  100% {
    -webkit-transform: translateY(0);
    transform: translateY(0);
  }
}
@-webkit-keyframes ball-pulse-sync {
  33% {
    -webkit-transform: translateY(10px);
    transform: translateY(10px);
  }
  66% {
    -webkit-transform: translateY(-10px);
    transform: translateY(-10px);
  }
  100% {
    -webkit-transform: translateY(0);
    transform: translateY(0);
  }
}
@-o-keyframes ball-pulse-sync {
  33% {
    -webkit-transform: translateY(10px);
    transform: translateY(10px);
  }
  66% {
    -webkit-transform: translateY(-10px);
    transform: translateY(-10px);
  }
  100% {
    -webkit-transform: translateY(0);
    transform: translateY(0);
  }
}
@keyframes ball-pulse-sync {
  33% {
    -webkit-transform: translateY(10px);
    transform: translateY(10px);
  }
  66% {
    -webkit-transform: translateY(-10px);
    transform: translateY(-10px);
  }
  100% {
    -webkit-transform: translateY(0);
    transform: translateY(0);
  }
}
@-moz-keyframes ball-scale {
  0% {
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-webkit-keyframes ball-scale {
  0% {
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-o-keyframes ball-scale {
  0% {
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@keyframes ball-scale {
  0% {
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-moz-keyframes ball-scale {
  0% {
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-webkit-keyframes ball-scale {
  0% {
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-o-keyframes ball-scale {
  0% {
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@keyframes ball-scale {
  0% {
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-moz-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@-webkit-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@-o-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@-moz-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@-webkit-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@-o-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@-moz-keyframes justRotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@-webkit-keyframes justRotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@-o-keyframes justRotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@keyframes justRotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@-moz-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg) scale(1);
    transform: rotate(0deg) scale(1);
  }
  50% {
    -webkit-transform: rotate(180deg) scale(0.6);
    transform: rotate(180deg) scale(0.6);
  }
  100% {
    -webkit-transform: rotate(360deg) scale(1);
    transform: rotate(360deg) scale(1);
  }
}
@-webkit-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg) scale(1);
    transform: rotate(0deg) scale(1);
  }
  50% {
    -webkit-transform: rotate(180deg) scale(0.6);
    transform: rotate(180deg) scale(0.6);
  }
  100% {
    -webkit-transform: rotate(360deg) scale(1);
    transform: rotate(360deg) scale(1);
  }
}
@-o-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg) scale(1);
    transform: rotate(0deg) scale(1);
  }
  50% {
    -webkit-transform: rotate(180deg) scale(0.6);
    transform: rotate(180deg) scale(0.6);
  }
  100% {
    -webkit-transform: rotate(360deg) scale(1);
    transform: rotate(360deg) scale(1);
  }
}
@keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg) scale(1);
    transform: rotate(0deg) scale(1);
  }
  50% {
    -webkit-transform: rotate(180deg) scale(0.6);
    transform: rotate(180deg) scale(0.6);
  }
  100% {
    -webkit-transform: rotate(360deg) scale(1);
    transform: rotate(360deg) scale(1);
  }
}
@-moz-keyframes scale {
  30% {
    -webkit-transform: scale(0.3);
    transform: scale(0.3);
  }
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-webkit-keyframes scale {
  30% {
    -webkit-transform: scale(0.3);
    transform: scale(0.3);
  }
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-o-keyframes scale {
  30% {
    -webkit-transform: scale(0.3);
    transform: scale(0.3);
  }
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@keyframes scale {
  30% {
    -webkit-transform: scale(0.3);
    transform: scale(0.3);
  }
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-moz-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg) scale(1);
    transform: rotate(0deg) scale(1);
  }
  50% {
    -webkit-transform: rotate(180deg) scale(0.6);
    transform: rotate(180deg) scale(0.6);
  }
  100% {
    -webkit-transform: rotate(360deg) scale(1);
    transform: rotate(360deg) scale(1);
  }
}
@-webkit-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg) scale(1);
    transform: rotate(0deg) scale(1);
  }
  50% {
    -webkit-transform: rotate(180deg) scale(0.6);
    transform: rotate(180deg) scale(0.6);
  }
  100% {
    -webkit-transform: rotate(360deg) scale(1);
    transform: rotate(360deg) scale(1);
  }
}
@-o-keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg) scale(1);
    transform: rotate(0deg) scale(1);
  }
  50% {
    -webkit-transform: rotate(180deg) scale(0.6);
    transform: rotate(180deg) scale(0.6);
  }
  100% {
    -webkit-transform: rotate(360deg) scale(1);
    transform: rotate(360deg) scale(1);
  }
}
@keyframes rotate {
  0% {
    -webkit-transform: rotate(0deg) scale(1);
    transform: rotate(0deg) scale(1);
  }
  50% {
    -webkit-transform: rotate(180deg) scale(0.6);
    transform: rotate(180deg) scale(0.6);
  }
  100% {
    -webkit-transform: rotate(360deg) scale(1);
    transform: rotate(360deg) scale(1);
  }
}
@-moz-keyframes ball-scale-ripple {
  0% {
    opacity: 1;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  70% {
    opacity: 0.7;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  100% {
    opacity: 0;
  }
}
@-webkit-keyframes ball-scale-ripple {
  0% {
    opacity: 1;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  70% {
    opacity: 0.7;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  100% {
    opacity: 0;
  }
}
@-o-keyframes ball-scale-ripple {
  0% {
    opacity: 1;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  70% {
    opacity: 0.7;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  100% {
    opacity: 0;
  }
}
@keyframes ball-scale-ripple {
  0% {
    opacity: 1;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  70% {
    opacity: 0.7;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  100% {
    opacity: 0;
  }
}
@-moz-keyframes ball-scale-ripple-multiple {
  0% {
    opacity: 1;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  70% {
    opacity: 0.7;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  100% {
    opacity: 0;
  }
}
@-webkit-keyframes ball-scale-ripple-multiple {
  0% {
    opacity: 1;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  70% {
    opacity: 0.7;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  100% {
    opacity: 0;
  }
}
@-o-keyframes ball-scale-ripple-multiple {
  0% {
    opacity: 1;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  70% {
    opacity: 0.7;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  100% {
    opacity: 0;
  }
}
@keyframes ball-scale-ripple-multiple {
  0% {
    opacity: 1;
    -webkit-transform: scale(0.1);
    transform: scale(0.1);
  }
  70% {
    opacity: 0.7;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  100% {
    opacity: 0;
  }
}
@-moz-keyframes ball-beat {
  50% {
    opacity: 0.2;
    -webkit-transform: scale(0.75);
    transform: scale(0.75);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-webkit-keyframes ball-beat {
  50% {
    opacity: 0.2;
    -webkit-transform: scale(0.75);
    transform: scale(0.75);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-o-keyframes ball-beat {
  50% {
    opacity: 0.2;
    -webkit-transform: scale(0.75);
    transform: scale(0.75);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@keyframes ball-beat {
  50% {
    opacity: 0.2;
    -webkit-transform: scale(0.75);
    transform: scale(0.75);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-moz-keyframes ball-scale-multiple {
  0% {
    opacity: 0;
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  5% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-webkit-keyframes ball-scale-multiple {
  0% {
    opacity: 0;
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  5% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-o-keyframes ball-scale-multiple {
  0% {
    opacity: 0;
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  5% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@keyframes ball-scale-multiple {
  0% {
    opacity: 0;
    -webkit-transform: scale(0);
    transform: scale(0);
  }
  5% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-moz-keyframes ball-triangle-path-1 {
  33% {
    -webkit-transform: translate(25px, -50px);
    transform: translate(25px, -50px);
  }
  66% {
    -webkit-transform: translate(50px, 0px);
    transform: translate(50px, 0px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@-webkit-keyframes ball-triangle-path-1 {
  33% {
    -webkit-transform: translate(25px, -50px);
    transform: translate(25px, -50px);
  }
  66% {
    -webkit-transform: translate(50px, 0px);
    transform: translate(50px, 0px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@-o-keyframes ball-triangle-path-1 {
  33% {
    -webkit-transform: translate(25px, -50px);
    transform: translate(25px, -50px);
  }
  66% {
    -webkit-transform: translate(50px, 0px);
    transform: translate(50px, 0px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@keyframes ball-triangle-path-1 {
  33% {
    -webkit-transform: translate(25px, -50px);
    transform: translate(25px, -50px);
  }
  66% {
    -webkit-transform: translate(50px, 0px);
    transform: translate(50px, 0px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@-moz-keyframes ball-triangle-path-2 {
  33% {
    -webkit-transform: translate(25px, 50px);
    transform: translate(25px, 50px);
  }
  66% {
    -webkit-transform: translate(-25px, 50px);
    transform: translate(-25px, 50px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@-webkit-keyframes ball-triangle-path-2 {
  33% {
    -webkit-transform: translate(25px, 50px);
    transform: translate(25px, 50px);
  }
  66% {
    -webkit-transform: translate(-25px, 50px);
    transform: translate(-25px, 50px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@-o-keyframes ball-triangle-path-2 {
  33% {
    -webkit-transform: translate(25px, 50px);
    transform: translate(25px, 50px);
  }
  66% {
    -webkit-transform: translate(-25px, 50px);
    transform: translate(-25px, 50px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@keyframes ball-triangle-path-2 {
  33% {
    -webkit-transform: translate(25px, 50px);
    transform: translate(25px, 50px);
  }
  66% {
    -webkit-transform: translate(-25px, 50px);
    transform: translate(-25px, 50px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@-moz-keyframes ball-triangle-path-3 {
  33% {
    -webkit-transform: translate(-50px, 0px);
    transform: translate(-50px, 0px);
  }
  66% {
    -webkit-transform: translate(-25px, -50px);
    transform: translate(-25px, -50px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@-webkit-keyframes ball-triangle-path-3 {
  33% {
    -webkit-transform: translate(-50px, 0px);
    transform: translate(-50px, 0px);
  }
  66% {
    -webkit-transform: translate(-25px, -50px);
    transform: translate(-25px, -50px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@-o-keyframes ball-triangle-path-3 {
  33% {
    -webkit-transform: translate(-50px, 0px);
    transform: translate(-50px, 0px);
  }
  66% {
    -webkit-transform: translate(-25px, -50px);
    transform: translate(-25px, -50px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@keyframes ball-triangle-path-3 {
  33% {
    -webkit-transform: translate(-50px, 0px);
    transform: translate(-50px, 0px);
  }
  66% {
    -webkit-transform: translate(-25px, -50px);
    transform: translate(-25px, -50px);
  }
  100% {
    -webkit-transform: translate(0px, 0px);
    transform: translate(0px, 0px);
  }
}
@-moz-keyframes ball-pulse-rise-even {
  0% {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }
  25% {
    -webkit-transform: translateY(-30px);
    transform: translateY(-30px);
  }
  50% {
    -webkit-transform: scale(0.4);
    transform: scale(0.4);
  }
  75% {
    -webkit-transform: translateY(30px);
    transform: translateY(30px);
  }
  100% {
    -webkit-transform: translateY(0);
    -webkit-transform: scale(1);
    transform: translateY(0);
    transform: scale(1);
  }
}
@-webkit-keyframes ball-pulse-rise-even {
  0% {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }
  25% {
    -webkit-transform: translateY(-30px);
    transform: translateY(-30px);
  }
  50% {
    -webkit-transform: scale(0.4);
    transform: scale(0.4);
  }
  75% {
    -webkit-transform: translateY(30px);
    transform: translateY(30px);
  }
  100% {
    -webkit-transform: translateY(0);
    -webkit-transform: scale(1);
    transform: translateY(0);
    transform: scale(1);
  }
}
@-o-keyframes ball-pulse-rise-even {
  0% {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }
  25% {
    -webkit-transform: translateY(-30px);
    transform: translateY(-30px);
  }
  50% {
    -webkit-transform: scale(0.4);
    transform: scale(0.4);
  }
  75% {
    -webkit-transform: translateY(30px);
    transform: translateY(30px);
  }
  100% {
    -webkit-transform: translateY(0);
    -webkit-transform: scale(1);
    transform: translateY(0);
    transform: scale(1);
  }
}
@keyframes ball-pulse-rise-even {
  0% {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }
  25% {
    -webkit-transform: translateY(-30px);
    transform: translateY(-30px);
  }
  50% {
    -webkit-transform: scale(0.4);
    transform: scale(0.4);
  }
  75% {
    -webkit-transform: translateY(30px);
    transform: translateY(30px);
  }
  100% {
    -webkit-transform: translateY(0);
    -webkit-transform: scale(1);
    transform: translateY(0);
    transform: scale(1);
  }
}
@-moz-keyframes ball-pulse-rise-odd {
  0% {
    -webkit-transform: scale(0.4);
    transform: scale(0.4);
  }
  25% {
    -webkit-transform: translateY(30px);
    transform: translateY(30px);
  }
  50% {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }
  75% {
    -webkit-transform: translateY(-30px);
    transform: translateY(-30px);
  }
  100% {
    -webkit-transform: translateY(0);
    -webkit-transform: scale(0.75);
    transform: translateY(0);
    transform: scale(0.75);
  }
}
@-webkit-keyframes ball-pulse-rise-odd {
  0% {
    -webkit-transform: scale(0.4);
    transform: scale(0.4);
  }
  25% {
    -webkit-transform: translateY(30px);
    transform: translateY(30px);
  }
  50% {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }
  75% {
    -webkit-transform: translateY(-30px);
    transform: translateY(-30px);
  }
  100% {
    -webkit-transform: translateY(0);
    -webkit-transform: scale(0.75);
    transform: translateY(0);
    transform: scale(0.75);
  }
}
@-o-keyframes ball-pulse-rise-odd {
  0% {
    -webkit-transform: scale(0.4);
    transform: scale(0.4);
  }
  25% {
    -webkit-transform: translateY(30px);
    transform: translateY(30px);
  }
  50% {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }
  75% {
    -webkit-transform: translateY(-30px);
    transform: translateY(-30px);
  }
  100% {
    -webkit-transform: translateY(0);
    -webkit-transform: scale(0.75);
    transform: translateY(0);
    transform: scale(0.75);
  }
}
@keyframes ball-pulse-rise-odd {
  0% {
    -webkit-transform: scale(0.4);
    transform: scale(0.4);
  }
  25% {
    -webkit-transform: translateY(30px);
    transform: translateY(30px);
  }
  50% {
    -webkit-transform: scale(1.1);
    transform: scale(1.1);
  }
  75% {
    -webkit-transform: translateY(-30px);
    transform: translateY(-30px);
  }
  100% {
    -webkit-transform: translateY(0);
    -webkit-transform: scale(0.75);
    transform: translateY(0);
    transform: scale(0.75);
  }
}
@-moz-keyframes ball-grid-beat {
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}
@-webkit-keyframes ball-grid-beat {
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}
@-o-keyframes ball-grid-beat {
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}
@keyframes ball-grid-beat {
  50% {
    opacity: 0.7;
  }
  100% {
    opacity: 1;
  }
}
@-moz-keyframes ball-grid-pulse {
  0% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    -webkit-transform: scale(0.5);
    transform: scale(0.5);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-webkit-keyframes ball-grid-pulse {
  0% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    -webkit-transform: scale(0.5);
    transform: scale(0.5);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-o-keyframes ball-grid-pulse {
  0% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    -webkit-transform: scale(0.5);
    transform: scale(0.5);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@keyframes ball-grid-pulse {
  0% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    -webkit-transform: scale(0.5);
    transform: scale(0.5);
  }
  100% {
    opacity: 1;
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-moz-keyframes ball-spin-loader {
  75% {
    opacity: 0.2;
  }
  100% {
    opacity: 1;
  }
}
@-webkit-keyframes ball-spin-loader {
  75% {
    opacity: 0.2;
  }
  100% {
    opacity: 1;
  }
}
@-o-keyframes ball-spin-loader {
  75% {
    opacity: 0.2;
  }
  100% {
    opacity: 1;
  }
}
@keyframes ball-spin-loader {
  75% {
    opacity: 0.2;
  }
  100% {
    opacity: 1;
  }
}
@-moz-keyframes ball-zig {
  33% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  66% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-webkit-keyframes ball-zig {
  33% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  66% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-o-keyframes ball-zig {
  33% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  66% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@keyframes ball-zig {
  33% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  66% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-moz-keyframes ball-zag {
  33% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  66% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-webkit-keyframes ball-zag {
  33% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  66% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-o-keyframes ball-zag {
  33% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  66% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@keyframes ball-zag {
  33% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  66% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-moz-keyframes ball-zig-deflect {
  17% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  34% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  50% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
  67% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  84% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-webkit-keyframes ball-zig-deflect {
  17% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  34% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  50% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
  67% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  84% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-o-keyframes ball-zig-deflect {
  17% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  34% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  50% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
  67% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  84% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@keyframes ball-zig-deflect {
  17% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  34% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  50% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
  67% {
    -webkit-transform: translate(15px, -30px);
    transform: translate(15px, -30px);
  }
  84% {
    -webkit-transform: translate(-15px, -30px);
    transform: translate(-15px, -30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-moz-keyframes ball-zag-deflect {
  17% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  34% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  50% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
  67% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  84% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-webkit-keyframes ball-zag-deflect {
  17% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  34% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  50% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
  67% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  84% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-o-keyframes ball-zag-deflect {
  17% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  34% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  50% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
  67% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  84% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@keyframes ball-zag-deflect {
  17% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  34% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  50% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
  67% {
    -webkit-transform: translate(-15px, 30px);
    transform: translate(-15px, 30px);
  }
  84% {
    -webkit-transform: translate(15px, 30px);
    transform: translate(15px, 30px);
  }
  100% {
    -webkit-transform: translate(0, 0);
    transform: translate(0, 0);
  }
}
@-moz-keyframes line-scale {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  50% {
    -webkit-transform: scaley(0.4);
    transform: scaley(0.4);
  }
  100% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
@-webkit-keyframes line-scale {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  50% {
    -webkit-transform: scaley(0.4);
    transform: scaley(0.4);
  }
  100% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
@-o-keyframes line-scale {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  50% {
    -webkit-transform: scaley(0.4);
    transform: scaley(0.4);
  }
  100% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
@keyframes line-scale {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  50% {
    -webkit-transform: scaley(0.4);
    transform: scaley(0.4);
  }
  100% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
@-moz-keyframes line-scale-party {
  0% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  50% {
    -webkit-transform: scale(0.5);
    transform: scale(0.5);
  }
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-webkit-keyframes line-scale-party {
  0% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  50% {
    -webkit-transform: scale(0.5);
    transform: scale(0.5);
  }
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-o-keyframes line-scale-party {
  0% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  50% {
    -webkit-transform: scale(0.5);
    transform: scale(0.5);
  }
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@keyframes line-scale-party {
  0% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
  50% {
    -webkit-transform: scale(0.5);
    transform: scale(0.5);
  }
  100% {
    -webkit-transform: scale(1);
    transform: scale(1);
  }
}
@-moz-keyframes line-scale-pulse-out {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  50% {
    -webkit-transform: scaley(0.4);
    transform: scaley(0.4);
  }
  100% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
@-webkit-keyframes line-scale-pulse-out {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  50% {
    -webkit-transform: scaley(0.4);
    transform: scaley(0.4);
  }
  100% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
@-o-keyframes line-scale-pulse-out {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  50% {
    -webkit-transform: scaley(0.4);
    transform: scaley(0.4);
  }
  100% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
@keyframes line-scale-pulse-out {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  50% {
    -webkit-transform: scaley(0.4);
    transform: scaley(0.4);
  }
  100% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
@-moz-keyframes line-scale-pulse-out-rapid {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  80% {
    -webkit-transform: scaley(0.3);
    transform: scaley(0.3);
  }
  90% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
@-webkit-keyframes line-scale-pulse-out-rapid {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  80% {
    -webkit-transform: scaley(0.3);
    transform: scaley(0.3);
  }
  90% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
@-o-keyframes line-scale-pulse-out-rapid {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  80% {
    -webkit-transform: scaley(0.3);
    transform: scaley(0.3);
  }
  90% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
@keyframes line-scale-pulse-out-rapid {
  0% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
  80% {
    -webkit-transform: scaley(0.3);
    transform: scaley(0.3);
  }
  90% {
    -webkit-transform: scaley(1);
    transform: scaley(1);
  }
}
@-moz-keyframes line-spin-fade-loader {
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 1;
  }
}
@-webkit-keyframes line-spin-fade-loader {
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 1;
  }
}
@-o-keyframes line-spin-fade-loader {
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 1;
  }
}
@keyframes line-spin-fade-loader {
  50% {
    opacity: 0.3;
  }
  100% {
    opacity: 1;
  }
}
@-moz-keyframes triangle-skew-spin {
  25% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(0);
    transform: perspective(100px) rotateX(180deg) rotateY(0);
  }
  50% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(180deg);
    transform: perspective(100px) rotateX(180deg) rotateY(180deg);
  }
  75% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(180deg);
    transform: perspective(100px) rotateX(0) rotateY(180deg);
  }
  100% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(0);
    transform: perspective(100px) rotateX(0) rotateY(0);
  }
}
@-webkit-keyframes triangle-skew-spin {
  25% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(0);
    transform: perspective(100px) rotateX(180deg) rotateY(0);
  }
  50% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(180deg);
    transform: perspective(100px) rotateX(180deg) rotateY(180deg);
  }
  75% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(180deg);
    transform: perspective(100px) rotateX(0) rotateY(180deg);
  }
  100% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(0);
    transform: perspective(100px) rotateX(0) rotateY(0);
  }
}
@-o-keyframes triangle-skew-spin {
  25% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(0);
    transform: perspective(100px) rotateX(180deg) rotateY(0);
  }
  50% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(180deg);
    transform: perspective(100px) rotateX(180deg) rotateY(180deg);
  }
  75% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(180deg);
    transform: perspective(100px) rotateX(0) rotateY(180deg);
  }
  100% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(0);
    transform: perspective(100px) rotateX(0) rotateY(0);
  }
}
@keyframes triangle-skew-spin {
  25% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(0);
    transform: perspective(100px) rotateX(180deg) rotateY(0);
  }
  50% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(180deg);
    transform: perspective(100px) rotateX(180deg) rotateY(180deg);
  }
  75% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(180deg);
    transform: perspective(100px) rotateX(0) rotateY(180deg);
  }
  100% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(0);
    transform: perspective(100px) rotateX(0) rotateY(0);
  }
}
@-moz-keyframes square-spin {
  25% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(0);
    transform: perspective(100px) rotateX(180deg) rotateY(0);
  }
  50% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(180deg);
    transform: perspective(100px) rotateX(180deg) rotateY(180deg);
  }
  75% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(180deg);
    transform: perspective(100px) rotateX(0) rotateY(180deg);
  }
  100% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(0);
    transform: perspective(100px) rotateX(0) rotateY(0);
  }
}
@-webkit-keyframes square-spin {
  25% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(0);
    transform: perspective(100px) rotateX(180deg) rotateY(0);
  }
  50% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(180deg);
    transform: perspective(100px) rotateX(180deg) rotateY(180deg);
  }
  75% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(180deg);
    transform: perspective(100px) rotateX(0) rotateY(180deg);
  }
  100% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(0);
    transform: perspective(100px) rotateX(0) rotateY(0);
  }
}
@-o-keyframes square-spin {
  25% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(0);
    transform: perspective(100px) rotateX(180deg) rotateY(0);
  }
  50% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(180deg);
    transform: perspective(100px) rotateX(180deg) rotateY(180deg);
  }
  75% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(180deg);
    transform: perspective(100px) rotateX(0) rotateY(180deg);
  }
  100% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(0);
    transform: perspective(100px) rotateX(0) rotateY(0);
  }
}
@keyframes square-spin {
  25% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(0);
    transform: perspective(100px) rotateX(180deg) rotateY(0);
  }
  50% {
    -webkit-transform: perspective(100px) rotateX(180deg) rotateY(180deg);
    transform: perspective(100px) rotateX(180deg) rotateY(180deg);
  }
  75% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(180deg);
    transform: perspective(100px) rotateX(0) rotateY(180deg);
  }
  100% {
    -webkit-transform: perspective(100px) rotateX(0) rotateY(0);
    transform: perspective(100px) rotateX(0) rotateY(0);
  }
}
@-moz-keyframes rotate_pacman_half_up {
  0% {
    -webkit-transform: rotate(270deg);
    transform: rotate(270deg);
  }
  50% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
  100% {
    -webkit-transform: rotate(270deg);
    transform: rotate(270deg);
  }
}
@-webkit-keyframes rotate_pacman_half_up {
  0% {
    -webkit-transform: rotate(270deg);
    transform: rotate(270deg);
  }
  50% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
  100% {
    -webkit-transform: rotate(270deg);
    transform: rotate(270deg);
  }
}
@-o-keyframes rotate_pacman_half_up {
  0% {
    -webkit-transform: rotate(270deg);
    transform: rotate(270deg);
  }
  50% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
  100% {
    -webkit-transform: rotate(270deg);
    transform: rotate(270deg);
  }
}
@keyframes rotate_pacman_half_up {
  0% {
    -webkit-transform: rotate(270deg);
    transform: rotate(270deg);
  }
  50% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
  100% {
    -webkit-transform: rotate(270deg);
    transform: rotate(270deg);
  }
}
@-moz-keyframes rotate_pacman_half_down {
  0% {
    -webkit-transform: rotate(90deg);
    transform: rotate(90deg);
  }
  50% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(90deg);
    transform: rotate(90deg);
  }
}
@-webkit-keyframes rotate_pacman_half_down {
  0% {
    -webkit-transform: rotate(90deg);
    transform: rotate(90deg);
  }
  50% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(90deg);
    transform: rotate(90deg);
  }
}
@-o-keyframes rotate_pacman_half_down {
  0% {
    -webkit-transform: rotate(90deg);
    transform: rotate(90deg);
  }
  50% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(90deg);
    transform: rotate(90deg);
  }
}
@keyframes rotate_pacman_half_down {
  0% {
    -webkit-transform: rotate(90deg);
    transform: rotate(90deg);
  }
  50% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  100% {
    -webkit-transform: rotate(90deg);
    transform: rotate(90deg);
  }
}
@-moz-keyframes pacman-balls {
  75% {
    opacity: 0.7;
  }
  100% {
    -webkit-transform: translate(-100px, -6.25px);
    transform: translate(-100px, -6.25px);
  }
}
@-webkit-keyframes pacman-balls {
  75% {
    opacity: 0.7;
  }
  100% {
    -webkit-transform: translate(-100px, -6.25px);
    transform: translate(-100px, -6.25px);
  }
}
@-o-keyframes pacman-balls {
  75% {
    opacity: 0.7;
  }
  100% {
    -webkit-transform: translate(-100px, -6.25px);
    transform: translate(-100px, -6.25px);
  }
}
@keyframes pacman-balls {
  75% {
    opacity: 0.7;
  }
  100% {
    -webkit-transform: translate(-100px, -6.25px);
    transform: translate(-100px, -6.25px);
  }
}
@-moz-keyframes cube-transition {
  25% {
    -webkit-transform: translateX(50px) scale(0.5) rotate(-90deg);
    transform: translateX(50px) scale(0.5) rotate(-90deg);
  }
  50% {
    -webkit-transform: translate(50px, 50px) rotate(-180deg);
    transform: translate(50px, 50px) rotate(-180deg);
  }
  75% {
    -webkit-transform: translateY(50px) scale(0.5) rotate(-270deg);
    transform: translateY(50px) scale(0.5) rotate(-270deg);
  }
  100% {
    -webkit-transform: rotate(-360deg);
    transform: rotate(-360deg);
  }
}
@-webkit-keyframes cube-transition {
  25% {
    -webkit-transform: translateX(50px) scale(0.5) rotate(-90deg);
    transform: translateX(50px) scale(0.5) rotate(-90deg);
  }
  50% {
    -webkit-transform: translate(50px, 50px) rotate(-180deg);
    transform: translate(50px, 50px) rotate(-180deg);
  }
  75% {
    -webkit-transform: translateY(50px) scale(0.5) rotate(-270deg);
    transform: translateY(50px) scale(0.5) rotate(-270deg);
  }
  100% {
    -webkit-transform: rotate(-360deg);
    transform: rotate(-360deg);
  }
}
@-o-keyframes cube-transition {
  25% {
    -webkit-transform: translateX(50px) scale(0.5) rotate(-90deg);
    transform: translateX(50px) scale(0.5) rotate(-90deg);
  }
  50% {
    -webkit-transform: translate(50px, 50px) rotate(-180deg);
    transform: translate(50px, 50px) rotate(-180deg);
  }
  75% {
    -webkit-transform: translateY(50px) scale(0.5) rotate(-270deg);
    transform: translateY(50px) scale(0.5) rotate(-270deg);
  }
  100% {
    -webkit-transform: rotate(-360deg);
    transform: rotate(-360deg);
  }
}
@keyframes cube-transition {
  25% {
    -webkit-transform: translateX(50px) scale(0.5) rotate(-90deg);
    transform: translateX(50px) scale(0.5) rotate(-90deg);
  }
  50% {
    -webkit-transform: translate(50px, 50px) rotate(-180deg);
    transform: translate(50px, 50px) rotate(-180deg);
  }
  75% {
    -webkit-transform: translateY(50px) scale(0.5) rotate(-270deg);
    transform: translateY(50px) scale(0.5) rotate(-270deg);
  }
  100% {
    -webkit-transform: rotate(-360deg);
    transform: rotate(-360deg);
  }
}
@-moz-keyframes spin-rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@-webkit-keyframes spin-rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@-o-keyframes spin-rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}
@keyframes spin-rotate {
  0% {
    -webkit-transform: rotate(0deg);
    transform: rotate(0deg);
  }
  50% {
    -webkit-transform: rotate(180deg);
    transform: rotate(180deg);
  }
  100% {
    -webkit-transform: rotate(360deg);
    transform: rotate(360deg);
  }
}

.side-menu {
  width: 248px;
  position: sticky;
  top: 0;
  height: 100%; overflow-y: auto;
  flex-shrink: 0;
  z-index: var(--z-index-side-menu);
  display: flex;
  flex-direction: column;
  background-color: var(--legacy-sidebar-background);
}

.side-menu .menu-header {
  display: none;
}

.side-menu .menu-content {
  height: calc(100% - 80px);
  overflow-y: auto;
}

.side-menu .page-tour-container {
  width: 136px;
  height: 32px;
  position: absolute;
  bottom: 24px;
  left: 61px;
}

.side-menu .menu-item {
  position: relative;
}

.side-menu .menu-item .category-name {
  min-width: 152px;
}

.side-menu .menu-item .subcategory-name {
  min-width: 139px;
}

.side-menu .menu-item .subcategory-name:has(> .sml) {
  display: inline-flex;
  align-items: center;
}

.side-menu .menu-item.menu-dropdown {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.side-menu .partner-companyFilter {
  margin: 15px auto;
  display: flex;
  justify-content: center;
}

.side-menu .partner-companyFilter .is-antd-select {
  width: 200px;
  max-width: 200px;
}

.side-menu .partner-companyFilter .ant-select-selector {
  color: var(--color-surface) !important;
  border-color: var(--color-surface);
  background-color: var(--ds-gray-1) !important;
}

.side-menu .partner-companyFilter .ant-select-selector .ant-select-selection-item {
  color: var(--color-surface) !important;
}

/* Open/focused search field: antd + ConfigProvider use --ds-gray-1 as text; same as selector bg (navy) */
.side-menu .partner-companyFilter .ant-select-selection-search-input {
  color: var(--color-surface) !important;
  caret-color: var(--color-surface);
}

.side-menu .partner-companyFilter .ant-select-selection-placeholder {
  color: rgba(255, 255, 255, 0.72) !important;
}

.side-menu .partner-companyFilter .ant-select-arrow {
  color: var(--color-surface);
  border-color: var(--color-surface) transparent transparent;
}

.side-menu .partner-companyFilter .ant-select-arrow svg path {
  fill: var(--color-surface) !important;
}

/* Sidebar company filter (.partner-companyFilter) uses inverted --ds-* for light navy sidebar only.
   In dark theme gray scale flips — old rule makes the trigger read as bright white vs nav. */
[data-theme='dark'] .side-menu .partner-companyFilter .ant-select-selector {
  color: var(--color-text) !important;
  border-color: var(--card-border-color);
  background: var(--color-surface-foreground) !important;
}

[data-theme='dark'] .side-menu .partner-companyFilter .ant-select-selector .ant-select-selection-item {
  color: var(--color-text) !important;
}

[data-theme='dark'] .side-menu .partner-companyFilter .ant-select-arrow {
  color: var(--color-text);
  border-color: var(--color-text) transparent transparent;
}

[data-theme='dark'] .side-menu .partner-companyFilter .ant-select-arrow svg path {
  fill: var(--color-text) !important;
}

[data-theme='dark'] .side-menu .partner-companyFilter .ant-select-selection-search-input {
  color: var(--color-text) !important;
  caret-color: var(--color-text);
}

[data-theme='dark'] .side-menu .partner-companyFilter .ant-select-selection-placeholder {
  color: rgba(255, 255, 255, 0.55) !important;
}

@media screen and (max-width: 1050px) {
  .side-menu {
    width: calc(100% - 30px);
    margin: 15px;
    border-radius: 20px;
    position: sticky; top: 15px; z-index: var(--z-index-side-menu);
    user-select: none;
    height: auto;
    align-self: start;
  }

  .side-menu .menu-header {
    color: var(--color-surface);
    padding: 10px;
    display: block !important;
    text-align: center;
    cursor: pointer;
  }

  .side-menu .menu-header i {
    margin-right: 10px;
  }

  .side-menu .menu-content {
    padding-bottom: 15px;
    display: none;
    height: auto;
  }

  .side-menu .menu-content.open {
    display: block;
  }
}

.card {
  height: 100%;
  padding: 25px;
  border-radius: 10px;
  position: relative;
  background: var(--color-surface);
  box-shadow: var(--shadow-1);
  text-decoration: none;
}

.card.title-on-top {
  position: relative;
}

.card.title-on-top .detail {
  width: 100%;
  padding: 0 25px;
  position: absolute;
  top: 25px;
}

.card.title-on-top h3.title {
  position: absolute;
  top: 25px;
  right: 0;
  left: 0;
  text-align: center;
}

.card.hoverable {
  cursor: pointer;
  transition: 0.2s linear;
  will-change: auto;
}

.card.hoverable:hover {
  box-shadow: 0 5px 15px 0 rgba(0, 0, 0, 0.2);
}

.card.hoverable:hover > .button {
  bottom: 23px;
}

.card.hoverable:focus h3.title,
.card.hoverable:active h3.title {
  text-decoration: none;
}

.card.with-loader,
.card .with-loader {
  min-height: calc(100% - 60px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.card.with-button {
  position: relative;
}

.card.with-button > .button {
  width: 25.725px;
  height: 20px;
  position: absolute;
  right: calc(50% - 12.862px);
  bottom: 20px;
  background-image: url('${STATIC_BASE_URL}/static/webapp/images/thin-right-arrow.svg');
  background-size: cover;
  transition: 0.2s linear;
}

@media screen and (max-width: 1390px) {
  .card.heartbeat .size-adjustable {
    font-size: 11px;
  }

  .card.heartbeat button {
    bottom: 5px;
  }
}

@media screen and (max-width: 1290px) {
  .card.heartbeat {
    padding: 10px;
  }
}

@media screen and (max-width: 965px) {
  .card.heartbeat {
    font-size: 12px;
  }
}

.card h3.title {
  color: var(--primary-color);
  font-weight: bold;
  font-size: 18px;
  margin: 0 0 5px 0;
  line-height: normal;
  text-transform: uppercase;
}

.card h3.title > span {
  font-weight: normal;
  text-transform: none;
}

.card img {
  width: 100%;
  height: auto;
}

.card .header {
  color: var(--primary-color);
  font-weight: normal;
  font-size: 23px;
  width: 100%;
  height: 68px;
  padding: 15px 25px;
  border-radius: 10px 10px 0 0;
  background-color: var(--contrast-light-color);
}

.card .header .smaller-text {
  font-size: 18px;
}

.card .header .product-info-icon {
  display: block;
  float: right;
  line-height: 2.5;
}

.card .header .product-info-icon img {
  width: 20px;
}

.card .ui-row {
  padding: 3px 0;
}

.card .ui-row > * {
  width: 50%;
  display: inline-block;
  vertical-align: middle;
}

.card .ui-row > *:last-child {
  font-weight: bold;
  text-align: right;
}

.card .value {
  color: var(--primary-color);
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.card .value .data {
  font-weight: bold;
}

.card .value .detail {
  font-weight: bold;
  text-transform: uppercase;
}

.card .value > * {
  text-align: center;
}

.card .value > *.data {
  font-size: 44px;
}

.card .value > *.data.big {
  font-size: 66px;
}

.card .value > *.data.very-big {
  font-size: 100px;
}

.card .value > *.detail {
  font-size: 14px;
}

.card .value > *.detail.medium-font {
  font-size: 15px;
}

.card .value > *.detail.large-font {
  font-size: 24px;
}

.card .value.two-values > * {
  height: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.card .value.two-values > * > * {
  text-align: center;
}

.card .value.two-values > * > *.data.big {
  font-size: 55px;
}

.card .value.two-values > * > *.detail.medium-font {
  font-size: 18px;
}
.card .details-block p {
  margin: 0;
}

.card .details-block h3 {
  color: var(--primary-color);
  font-weight: bold;
  font-size: 14px;
  margin: 0 0 5px 0;
}

.is-table-header-c {
  color: var(--ds-gray-1);
  font-weight: bold;
  background-color: #08314d;
  text-align: center;
}

.is-table {
  height: auto;
  min-height: calc(100% - 30px);
  border-radius: 10px;
  overflow: hidden;
  background: var(--color-surface);
  box-shadow: var(--shadow-1);
}

.is-table .select-all-container .select-all-popup-action {
  color: var(--primary-color);
  text-decoration: underline;
  cursor: pointer;
}

.is-table .select-all-container .select-all-popup-action:hover {
  background-color: var(--contrast-light-color);
}

.is-table .is-table-button {
  color: var(--sub-text-color);
  height: 32px;
  padding: 0 20px;
  border: 1px solid var(--card-border-color);
  border-radius: 5px;
  display: inline-flex;
  vertical-align: top;
  background-color: var(--color-surface);
  cursor: pointer;
  user-select: none;
  margin: 4px;
}

.is-table .is-table-button.is-checkbox-counter {
  padding: 6px 10px;
  position: relative;
}

.is-table .is-table-button.is-checkbox-counter.highlight {
  color: var(--button-primary-text-default);
  background-color: var(--primary);
  border-color: var(--primary);
}

.is-table .is-table-button.is-checkbox-counter.highlight .checkbox-label {
  color: var(--button-primary-text-default);
}

.is-table .is-table-button.is-checkbox-counter.highlight .checkbox-custom {
  border-color: var(--button-primary-text-default);
  background-color: var(--button-primary-text-default);
}

.is-table .is-table-button.is-checkbox-counter.highlight .checkbox-custom:hover {
  border-color: var(--button-primary-text-default);
  background-color: var(--button-primary-text-default);
}

.is-table .is-table-button.is-checkbox-counter.highlight .checkbox-icon,
.is-table .is-table-button.is-checkbox-counter.highlight .checkbox-icon path {
  fill: var(--primary);
}

.is-table .is-table-button.is-checkbox-counter.highlight .minus-icon {
  background-color: var(--primary) !important;
}

.is-table .is-table-button.is-checkbox-counter.disabled .disabled-container {
  display: block;
}

.is-table .is-table-button.is-checkbox-counter .disabled-container {
  position: absolute;
  top: -5px;
  right: -5px;
  bottom: -5px;
  left: -5px;
  z-index: 1;
  display: none;
  background: var(--color-surface);
  opacity: 0.7;
}

.is-table.paginated {
  min-height: calc(100% - 77px);
  margin-bottom: 15px;
}

.is-table .is-table-header {
  padding: 15px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  text-align: right;
}

.is-table .is-table-header .is-search-field {
  max-width: 250px;
  float: left;
}

.is-table .is-table-header a.csv-button {
  display: inline-block;
}

.is-table .is-table-message {
  font-weight: 600;
  margin: 15px 15px 0 15px;
  padding: 15px;
  border-radius: 10px;
  box-shadow: 0 3px 10px rgba(0, 0, 0, 0.1);
  text-align: center;
}

.is-table .is-table-message.error {
  color: var(--red);
}

.is-table .is-table-content {
  min-height: 100%;
  padding: 15px;
}

.is-table .is-table-content table {
  color: var(--primary-color);
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.is-table .is-table-content table thead {
  color: var(--primary-color);
}

.is-table .is-table-content table thead th {
  font-weight: 600;
  padding: 0 0 10px 10px;
  vertical-align: top;
  text-align: left;
}

.is-table .is-table-content table tbody tr {
  transition: 0.3s linear all;
}

.is-table .is-table-content table tbody tr:nth-child(odd) {
  background-color: var(--is-table-row-odd-bg);
}

.is-table .is-table-content table tbody tr td {
  padding: 10px 5px;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.is-table .is-table-content table tbody tr td img,
.is-table .is-table-content table tbody tr td a {
  width: 20px;
  height: 20px;
  margin-right: 15px;
  vertical-align: middle;
  cursor: pointer;
}

[data-theme='dark'] .is-table .is-table-content table tbody tr td img[src*='button-icons/'] {
  filter: var(--table-action-icon-filter);
}

.is-table .is-table-content table tbody tr td img.inline-link,
.is-table .is-table-content table tbody tr td a.inline-link {
  margin-right: 0 !important;
  vertical-align: baseline !important;
}

/* ScrollableTable: keep column widths content-driven. Global .is-table-content table is
   table-layout:fixed, which clips Status (nowrap labels) into Mailbox in the drawer. */
.is-table .is-table-content table.is-scrollable-table {
  table-layout: auto;
}

.is-table .is-table-content table tbody tr {
  color: var(--primary-color);
}

.is-table .is-table-content table tbody tr.loader td {
  display: none;
}

.is-table .is-table-content table tbody tr.loader td.is-table-loader-container {
  height: 45px;
  padding: 0;
  display: table-cell;
  overflow: hidden;
}

.is-table .is-table-content table tbody tr.loader td.is-table-loader-container .loader-container .loader {
  top: calc(50% - 10px);
  left: calc(50% + 13.5px);
}

.is-table .is-table-content table tbody tr .is-table-loader-container {
  display: none;
}

.is-table .is-table-content table .is-last {
  opacity: 0.5;
}

.is-table .is-table-content table .show-all-affected-mailboxes {
  color: var(--button-primary-text-default);
  font-size: 12px;
  padding: 5px 16px;
  border-radius: 27px;
  position: absolute;
  bottom: 10px;
  left: calc(50% - 95px);
  background-color: var(--primary);
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.is-table .is-table-content table .show-all-affected-mailboxes:hover {
  background-color: var(--button-primary-bg-hover);
}

.is-table .is-table-content .is-table-loader {
  margin-top: 0;
  min-height: calc(100vh - var(--navigation-height, 70px) - 68px);
  display: flex;
  align-items: center;
  justify-content: center;
}

.is-table .is-table-content .is-table-loader .loader-container {
  height: auto;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.is-table .is-table-content .is-table-loader .loader-container > * {
  position: static;
  transform: none;
}

@media screen and (max-width: 900px) {
  .is-table th,
  .is-table td {
    width: 100%;
    display: block !important;
  }

  /*
    ScrollableTable (Manage & review / Affected mailboxes / Links / Attachments) keeps real
    columns and scrolls horizontally. The block stacking above turns every header/cell into a
    full-width stack and makes labels look "vertical" — restore table-cell layout for those tables.
    Do not force width:100% (from the rule above) or table-layout:fixed: Status is nowrap and
    would overflow into Mailbox. table-layout:auto + width:auto lets columns size to content.
  */
  .is-table table.is-scrollable-table {
    table-layout: auto !important;
  }
  .is-table table.is-scrollable-table th,
  .is-table table.is-scrollable-table td {
    width: auto !important;
    display: table-cell !important;
  }
}

table.is-table-pdf {
  table-layout: fixed;
}

table.is-table-pdf > tbody > tr > td + td {
  border-left-width: 0;
}

table.is-table-pdf > tbody > tr + tr > td {
  border-top-width: 0;
}

table.is-table-pdf {
  font-size: 8px;
  width: 100%;
}

table.is-table-pdf td {
  padding: 10px;
  border: 1px solid #000;
}

.pdf-mitigation-blocks {
  font-size: 8px;
}

.pdf-mitigation-blocks .p {
  padding: 10px;
}

.pdf-mitigation-blocks .fvc {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.pdf-mitigation-blocks * {
  border: 1px solid #000;
}

.pdf-mitigation-blocks > div {
  margin-bottom: 10px;
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.pdf-mitigation-blocks > div:last-child {
  margin-bottom: 0;
}

.pdf-mitigation-blocks > div > div {
  width: 30%;
}

.pdf-mitigation-blocks > div > div:last-child {
  width: 70%;
}

.pdf-mitigation-blocks .value-row {
  display: flex;
  align-items: stretch;
  justify-content: stretch;
}

.pdf-mitigation-blocks .value-row > div {
  width: 50%;
}

`
